'use client';

import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useConnectorStore } from '@/lib/connectors/store';
import type { ConnectorOperation, PaginationConfig } from '@/lib/connectors/types';

interface ResponseMapperProps {
  connectorId: string;
  operation: ConnectorOperation;
}

const PAGINATION_TYPES = [
  { value: 'none', label: 'No Pagination' },
  { value: 'offset', label: 'Offset-based' },
  { value: 'cursor', label: 'Cursor-based' },
  { value: 'page', label: 'Page-based' },
];

export function ResponseMapper({ connectorId, operation }: ResponseMapperProps) {
  const { updateOperation } = useConnectorStore();

  const schema = operation.response_schema || {};
  const pagination = operation.pagination_config;
  const paginationType = pagination?.type || 'none';

  const handleSchemaChange = async (schemaStr: string) => {
    try {
      const parsed = JSON.parse(schemaStr);
      await updateOperation(connectorId, operation.id, { response_schema: parsed });
    } catch {
      // Invalid JSON, don't update
    }
  };

  const handlePaginationTypeChange = async (value: string) => {
    try {
      if (value === 'none') {
        await updateOperation(connectorId, operation.id, { pagination_config: undefined });
      } else {
        await updateOperation(connectorId, operation.id, {
          pagination_config: {
            type: value as 'offset' | 'cursor' | 'page',
            limit_param: 'limit',
          },
        });
      }
    } catch (error) {
      console.error('Failed to update pagination:', error);
    }
  };

  const handlePaginationUpdate = async (updates: Partial<PaginationConfig>) => {
    if (!pagination) return;
    try {
      await updateOperation(connectorId, operation.id, {
        pagination_config: { ...pagination, ...updates },
      });
    } catch (error) {
      console.error('Failed to update pagination:', error);
    }
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle>Response Schema</CardTitle>
          <CardDescription>
            Define the expected response structure
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>JSON Schema</Label>
            <Textarea
              value={JSON.stringify(schema, null, 2)}
              onChange={(e) => handleSchemaChange(e.target.value)}
              placeholder={'{\n  "type": "object",\n  "properties": {\n    "data": { "type": "array" }\n  }\n}'}
              className="font-mono text-sm min-h-[200px]"
            />
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Pagination</CardTitle>
          <CardDescription>
            Configure pagination handling for list operations
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label>Pagination Type</Label>
            <Select value={paginationType} onValueChange={handlePaginationTypeChange}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {PAGINATION_TYPES.map((pt) => (
                  <SelectItem key={pt.value} value={pt.value}>
                    {pt.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {paginationType !== 'none' && pagination && (
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Limit Parameter</Label>
                <Input
                  value={pagination.limit_param || ''}
                  onChange={(e) => handlePaginationUpdate({ limit_param: e.target.value })}
                  placeholder="limit"
                  className="font-mono"
                />
              </div>

              {paginationType === 'offset' && (
                <div className="space-y-2">
                  <Label>Offset Parameter</Label>
                  <Input
                    value={pagination.offset_param || ''}
                    onChange={(e) => handlePaginationUpdate({ offset_param: e.target.value })}
                    placeholder="offset"
                    className="font-mono"
                  />
                </div>
              )}

              {paginationType === 'cursor' && (
                <>
                  <div className="space-y-2">
                    <Label>Cursor Parameter</Label>
                    <Input
                      value={pagination.cursor_param || ''}
                      onChange={(e) => handlePaginationUpdate({ cursor_param: e.target.value })}
                      placeholder="cursor"
                      className="font-mono"
                    />
                  </div>
                  <div className="space-y-2 col-span-2">
                    <Label>Cursor Path in Response</Label>
                    <Input
                      value={pagination.cursor_path || ''}
                      onChange={(e) => handlePaginationUpdate({ cursor_path: e.target.value })}
                      placeholder="meta.next_cursor"
                      className="font-mono"
                    />
                  </div>
                </>
              )}

              {paginationType === 'page' && (
                <div className="space-y-2">
                  <Label>Page Parameter</Label>
                  <Input
                    value={pagination.page_param || ''}
                    onChange={(e) => handlePaginationUpdate({ page_param: e.target.value })}
                    placeholder="page"
                    className="font-mono"
                  />
                </div>
              )}

              <div className="space-y-2">
                <Label>Total Count Path (optional)</Label>
                <Input
                  value={pagination.total_path || ''}
                  onChange={(e) => handlePaginationUpdate({ total_path: e.target.value })}
                  placeholder="meta.total"
                  className="font-mono"
                />
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
